import { Box, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { AlertCard } from 'components/AlertCard';
import { formatToDateTime, formatToDateTimeLongFormat } from 'utils/datetime';
import { CopyIcon } from 'components/Icons/CopyIcon';
import {
  CandidateEmailCorrespondence,
  CorrespondenceStatus,
  EmailCorrespondenceType
} from 'services/candidate/types';
import RestoreIcon from '@mui/icons-material/Restore';
import PauseIcon from '@mui/icons-material/Pause';
import { useMemo } from 'react';

type Props = {
  emailCorrespondence: CandidateEmailCorrespondence;
  onPauseEmailCorrespondenceClicked: (id: string) => void;
  onTriggerSendClicked: (id: string) => void;
  onRestoreEmailCorrespondenceClicked: (id: string) => void;
  isSending: boolean;
  readOnly?: boolean;
};

const emailBodyContainer: SxProps = {
  backgroundColor: 'customGrey.light',
  borderColor: 'customGrey.medium',
  borderStyle: 'solid',
  borderWidth: '1px',
  p: 2
};

const footerContainer: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

export const EmailCorrespondenceMessage: React.FC<Props> = ({
  emailCorrespondence,
  onPauseEmailCorrespondenceClicked,
  onTriggerSendClicked,
  onRestoreEmailCorrespondenceClicked,
  isSending,
  readOnly = false
}) => {
  const { id, status } = emailCorrespondence;

  const emailTypeTitle = useMemo(() => {
    if (
      emailCorrespondence?.type ===
      EmailCorrespondenceType.JOB_RECOMMENDATION_EMAIL
    ) {
      return 'Job recommendation message';
    } else {
      return 'Rejection message';
    }
  }, [emailCorrespondence]);

  const {
    title,
    type,
    message
  }: {
    title: string;
    type: 'info' | 'warning' | 'error' | 'success';
    message?: string;
  } = useMemo(() => {
    if (emailCorrespondence?.status === CorrespondenceStatus.SCHEDULED) {
      const { scheduledAt } = emailCorrespondence;

      if (scheduledAt) {
        const datetime = formatToDateTimeLongFormat(scheduledAt);

        return {
          title: `Scheduled to send`,
          type: 'warning',
          message: `This email will be sent on ${datetime}`
        };
      }

      return {
        title: 'Scheduled to send via Popp.',
        type: 'warning'
      };
    }

    if (emailCorrespondence?.status === CorrespondenceStatus.PAUSED) {
      return {
        title: 'You have stopped this email from being sent.',
        type: 'error'
      };
    }

    if (emailCorrespondence?.status === CorrespondenceStatus.SENT) {
      const { sentAt } = emailCorrespondence;

      if (sentAt) {
        const datetime = formatToDateTimeLongFormat(sentAt);

        return {
          title: `Sent ${datetime} via Popp.`,
          type: 'success'
        };
      }

      return {
        title: 'This email has been sent.',
        type: 'success'
      };
    }

    if (emailCorrespondence?.status === CorrespondenceStatus.CREATED) {
      return {
        title: 'This email will be scheduled soon.',
        type: 'info'
      };
    }

    return { title: '', type: 'info' };
  }, [emailCorrespondence?.status, emailCorrespondence?.scheduledAt]);

  return (
    <>
      <Typography variant="h6" fontWeight={'bold'}>
        {emailTypeTitle}
      </Typography>
      <AlertCard type={type} title={title} subtitle={message} />

      <Box sx={emailBodyContainer}>
        <Typography>
          <Box component="span" sx={{ color: 'text.secondary', mr: 2 }}>
            Subject
          </Box>
          {emailCorrespondence.subject}
        </Typography>
        <Typography variant="body1" whiteSpace="pre-line">
          {emailCorrespondence.body}
        </Typography>

        <Box sx={footerContainer}>
          <Typography variant="caption">
            Generated on {formatToDateTime(emailCorrespondence.createdAt)}
          </Typography>
          <CopyIcon copyValue={emailCorrespondence.body} />
        </Box>
      </Box>

      {!readOnly && status !== CorrespondenceStatus.SENT && (
        <Box display="flex" gap={2}>
          <Box>
            <Button
              onClick={() => {
                onTriggerSendClicked(id);
              }}
              variant="primary"
              disabled={isSending}
            >
              Send now
            </Button>
          </Box>
          {status === CorrespondenceStatus.PAUSED ? (
            <Button
              startIcon={<RestoreIcon />}
              onClick={() => onRestoreEmailCorrespondenceClicked(id)}
              variant="tertiary"
            >
              Restore this email
            </Button>
          ) : (
            <Button
              startIcon={<PauseIcon />}
              onClick={() => onPauseEmailCorrespondenceClicked(id)}
              variant="tertiary"
            >
              Pause this email
            </Button>
          )}
        </Box>
      )}
    </>
  );
};
